import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plane, ExternalLink } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec, num } from "@/lib/formatters";

const HBar = ({ rows, color, testId, total }) => (
  <div className="h-[300px]" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" barCategoryGap="25%" margin={{ right: 48 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" horizontal={false} />
        <XAxis type="number" stroke="#8AA4BF" fontSize={11} tickFormatter={fmt} />
        <YAxis type="category" dataKey="name" width={190} stroke="#8AA4BF" fontSize={11} interval={0} />
        <Tooltip formatter={(v) => `${fmt(v)} personar (${total ? dec(((v / total) * 100), 1) : "–"} %)`} contentStyle={tooltipStyle} />
        <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} name="Lønstakarar">
          <LabelList dataKey="count" position="right" formatter={fmt} fontSize={11} fill="#1F2A36" />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const KorttidSection = ({ data }) => {
  if (!data?.latest) return null;
  const lt = data.latest;
  const land = data.land_latest;
  const history = (data.history || []).map((h) => ({ year: h.year, [data.fylke_name]: h.fylke_pct, Landet: h.land_pct }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="korttid-card" id="korttid">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2 flex-wrap">
          <Plane className="w-5 h-5 text-[#103766]" />
          Sysselsette på korttidsopphald i {data.fylke_name}
          <Badge variant="outline" className="border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[10px] uppercase" data-testid="korttid-fylke-badge">Fylkestal</Badge>
        </CardTitle>
        <p className="text-sm text-[#4B647F]">
          Lønstakarar som jobbar i Noreg utan å vere registrert busett (korttidsopphald under seks månader, pendlarar frå utlandet m.m.).
          SSB publiserer desse tala berre per fylke – dei inngår ikkje i kommunens arbeidsplass- og pendlingstal over, som berre omfattar registrert busette.
        </p>
      </CardHeader>
      <CardContent className="pt-6 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="korttid-antall">
            <p className="text-xs text-[#8AA4BF]">Ikkje-busette lønstakarar {lt.year}</p>
            <p className="text-2xl font-light text-[#1F2A36]">{fmt(lt.ikke_bosatte)}</p>
            <p className="text-[11px] text-[#4B647F]">i {data.fylke_name}, 4. kvartal</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="korttid-andel">
            <p className="text-xs text-[#8AA4BF]">Andel av alle sysselsette i fylket</p>
            <p className="text-2xl font-light text-[#1F2A36]">{dec(lt.andel_pct, 1)} %</p>
            <p className="text-[11px] text-[#3F8A86]">{land ? `Landet: ${dec(land.andel_pct, 1)} %` : ""}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="korttid-topp-land">
            <p className="text-xs text-[#8AA4BF]">Største opphavsland</p>
            <p className="text-2xl font-light text-[#1F2A36]">{lt.land?.[0]?.name || "—"}</p>
            <p className="text-[11px] text-[#4B647F]">{lt.land?.[0] ? `${fmt(lt.land[0].count)} personar (${((lt.land[0].count / lt.ikke_bosatte) * 100).toFixed(0)} %)` : ""}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="korttid-topp-naering">
            <p className="text-xs text-[#8AA4BF]">Største næring</p>
            <p className="text-2xl font-light text-[#1F2A36] leading-tight">{lt.naering?.[0]?.name || "—"}</p>
            <p className="text-[11px] text-[#4B647F]">{lt.naering?.[0] ? `${fmt(lt.naering[0].count)} personar` : ""}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Kva land kjem dei frå? ({lt.year})</p>
            <HBar rows={lt.land.slice(0, 10)} color="#103766" testId="korttid-land-chart" total={lt.ikke_bosatte} />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Kva næringar jobbar dei i? ({lt.year})</p>
            <HBar rows={(lt.naering || []).slice(0, 10)} color="#3F8A86" testId="korttid-naering-chart" total={lt.ikke_bosatte} />
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Andel ikkje-busette lønstakarar av alle sysselsette, {history[0]?.year}–{lt.year}</p>
          <div className="h-[240px]" data-testid="korttid-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history}>
                {endringsLinjer(history)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={11} />
                <YAxis stroke="#8AA4BF" fontSize={11} tickFormatter={(v) => `${num(v)} %`} width={45} />
                <Tooltip formatter={(v) => (v == null ? "—" : `${dec(v, 1)} %`)} contentStyle={tooltipStyle} />
                <Legend itemSorter={null} />
                <Line type="monotone" dataKey={data.fylke_name} stroke="#103766" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="Landet" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-[#8AA4BF] mt-1">Fylkestal finst berre frå året fylket blei oppretta med dagens grenser; landstalet går lenger tilbake.</p>
        </div>
        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB <a href="https://www.ssb.no/statbank/table/11610" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">11610 <ExternalLink className="w-3 h-3" /></a> (landbakgrunn, sysselsette 20–66 år) og{" "}
          <a href="https://www.ssb.no/statbank/table/11612" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">11612 <ExternalLink className="w-3 h-3" /></a> (næring, 15–74 år), 4. kvartal. «Noreg» = norske statsborgarar/norskfødde som ikkje er registrert busett.
        </p>
      </CardContent>
    </Card>
  );
};

export default KorttidSection;
